import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import { TipJar } from '../components/TipJar';
import { ParticlesBackground, FloatingSymbols, IshtarStar, HypnoticVortex } from '../components/BabylonianEffects';
import { HomeSEO } from '../components/SEO';
import { DreamInterpreter } from '../components/DreamInterpreter';
import { BugTrackerWidget } from '../components/BugTrackerWidget';
import { BulletinBoard } from '../components/BulletinBoard';
import { 
  Sparkles, Moon, Sun, Heart, Star, Users, Scroll, ArrowRight, Eye,
  Calculator, Layers, Hash, Zap, Share2, Gamepad2, CloudMoon
} from 'lucide-react';

const FeatureCard = ({ icon: Icon, title, description, to, accent = 'gold', testId, symbol }) => {
  const accents = {
    gold: 'from-babylon-gold/10 to-babylon-obsidian border-babylon-gold/20 hover:border-babylon-gold/50',
    violet: 'from-babylon-amethyst/10 to-babylon-obsidian border-babylon-amethyst/20 hover:border-babylon-amethyst/50',
    purple: 'from-babylon-violet/10 to-babylon-obsidian border-babylon-violet/20 hover:border-babylon-violet/50',
    blood: 'from-babylon-blood/10 to-babylon-obsidian border-babylon-blood/20 hover:border-babylon-blood/50'
  };
  
  return (
    <Link to={to} data-testid={testId}>
      <motion.div
        whileHover={{ y: -8, scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        className={`glass-card p-5 bg-gradient-to-br ${accents[accent]} transition-all cursor-pointer h-full relative overflow-hidden group`}
      >
        {/* Background symbol */}
        <span className="absolute -right-4 -bottom-4 text-6xl text-babylon-gold/5 group-hover:text-babylon-gold/10 transition-all duration-500">
          {symbol}
        </span>
        
        {/* Hover glow */}
        <motion.div 
          className="absolute inset-0 bg-gradient-to-br from-babylon-gold/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"
        />
        
        <div className="relative z-10">
          <div className="flex items-center gap-3 mb-3">
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
              accent === 'gold' ? 'bg-babylon-gold/20' : 
              accent === 'violet' ? 'bg-babylon-amethyst/20' : 
              accent === 'blood' ? 'bg-babylon-blood/20' : 
              'bg-babylon-violet/20'
            }`}>
              <Icon className={`w-5 h-5 ${
                accent === 'gold' ? 'text-babylon-gold' : 
                accent === 'violet' ? 'text-babylon-amethyst' : 
                accent === 'blood' ? 'text-babylon-blood' : 
                'text-babylon-violet'
              }`} />
            </div>
          </div>
          <h3 className="font-heading text-lg text-babylon-text mb-1">{title}</h3>
          <p className="text-babylon-text-dim text-xs leading-relaxed">{description}</p>
        </div>
      </motion.div>
    </Link>
  );
};

export const HomePage = () => {
  const { user } = useAuth();

  const features = [
    { icon: Sun, title: 'Cardology', description: 'Discover your destiny through ancient card wisdom', to: '/calculator', accent: 'gold', testId: 'feature-cardology', symbol: '☉' },
    { icon: Eye, title: 'Oracle', description: 'Seek divine guidance from the cosmic intelligence', to: '/oracle', accent: 'violet', testId: 'feature-oracle', symbol: '𒀭' },
    { icon: Moon, title: 'Tarot Reading', description: '19+ spreads • 3 sacred deck traditions', to: '/tarot', accent: 'violet', testId: 'feature-tarot', symbol: '☽' },
    { icon: Gamepad2, title: 'Cosmic Quest', description: 'Embark on an epic RPG adventure', to: '/games/rpg', accent: 'purple', testId: 'feature-rpg', symbol: '⚔' }
  ];

  return (
    <div className="min-h-screen cosmic-bg relative">
      <HomeSEO />
      
      {/* Babylonian Background Effects */}
      <ParticlesBackground />
      <FloatingSymbols />
      
      {/* Bulletin Board - Fixed Left Side */}
      <BulletinBoard />
      
      {/* T3B3TU Credit */}
      <motion.div 
        className="fixed top-16 left-3 z-40 text-[10px] text-babylon-gold/70 font-mono tracking-wider"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1 }}
      >
        T3B3TU
      </motion.div>

      {/* Hero Section with Hypnotic Vortex */}
      <section className="relative z-10 px-4 sm:px-6 pt-8 sm:pt-16 pb-20 max-w-5xl mx-auto text-center">
        <HypnoticVortex className="opacity-30" />
        
        <motion.div 
          initial={{ opacity: 0, y: 30 }} 
          animate={{ opacity: 1, y: 0 }} 
          transition={{ duration: 0.8, ease: "easeOut" }}
          className="relative z-10"
        >
          {/* Ancient badge */}
          <motion.div 
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-babylon-obsidian/80 border border-babylon-gold/30 mb-8"
            animate={{ 
              boxShadow: [
                '0 0 20px rgba(201, 162, 39, 0.1)',
                '0 0 40px rgba(201, 162, 39, 0.2)',
                '0 0 20px rgba(201, 162, 39, 0.1)'
              ]
            }}
            transition={{ duration: 3, repeat: Infinity }}
          >
            <IshtarStar size="sm" animated={false} />
            <span className="text-xs text-babylon-text-dim tracking-wider uppercase">Ancient Mesopotamian Wisdom</span>
            <IshtarStar size="sm" animated={false} />
          </motion.div>

          {/* Main Title */}
          <motion.h1 
            className="font-heading text-5xl sm:text-6xl lg:text-7xl mb-6 leading-tight"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <span className="gold-text">CosmiCards</span>
          </motion.h1>
          
          {/* Cuneiform decoration */}
          <div className="flex items-center justify-center gap-4 mb-6">
            <span className="text-babylon-gold/40">𒄑</span>
            <span className="text-babylon-gold/60">𒀭</span>
            <span className="text-babylon-gold/40">𒊹</span>
            <span className="text-babylon-gold/60">𒀭</span>
            <span className="text-babylon-gold/40">𒄑</span>
          </div>
          
          <motion.p 
            className="text-base sm:text-lg text-babylon-text-dim mb-10 max-w-2xl mx-auto leading-relaxed"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
          >
            Peer through the veil of time. Divine your destiny through sacred cards, 
            celestial numerology, and the ancient arts of Babylon.
          </motion.p>

          <motion.div 
            className="flex flex-col sm:flex-row gap-4 justify-center"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
          >
            <Link to="/tarot" data-testid="start-reading-btn">
              <Button className="btn-primary h-12 px-8 text-sm gap-2 w-full sm:w-auto hypnotic-glow group">
                <Eye size={18} className="group-hover:animate-pulse" />
                Enter the Oracle
                <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
              </Button>
            </Link>
            <Link to="/calculations" data-testid="calculations-btn">
              <Button variant="ghost" className="btn-ghost h-12 px-8 text-sm gap-2 w-full sm:w-auto">
                <span className="text-babylon-gold">𒀭</span> Sacred Calculations
              </Button>
            </Link>
          </motion.div>
        </motion.div>
      </section>

      {/* Features Grid */}
      <section className="relative z-10 px-4 sm:px-6 py-16 max-w-5xl mx-auto">
        <motion.div 
          className="text-center mb-10"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
        >
          <h2 className="font-heading text-2xl text-babylon-text mb-2">The Sacred Arts</h2>
          <p className="text-babylon-text-dim text-sm">Choose your path of divination</p>
        </motion.div>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {features.map((feature, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.1 }}
            >
              <FeatureCard {...feature} />
            </motion.div>
          ))}
        </div>
      </section>

      {/* Dream Seer Section */}
      {user && (
        <section className="relative z-10 px-4 sm:px-6 py-12 max-w-3xl mx-auto">
          <motion.div 
            className="text-center mb-6"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
          >
            <div className="flex items-center justify-center gap-3 mb-3">
              <CloudMoon className="w-6 h-6 text-babylon-amethyst" />
              <h2 className="font-heading text-2xl text-babylon-text">Dream Seer</h2>
            </div>
            <p className="text-babylon-text-dim text-sm">Unveil the hidden meanings within your dreams</p>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
          >
            <DreamInterpreter />
          </motion.div>
        </section>
      )}

      {/* Quick Stats with Babylonian styling */}
      <section className="relative z-10 px-4 sm:px-6 py-12 max-w-5xl mx-auto">
        <motion.div 
          className="glass-card p-8 relative overflow-hidden"
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
        >
          {/* Background decoration */}
          <div className="absolute inset-0 opacity-5">
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-[200px] text-babylon-gold">
              𒀭
            </div>
          </div>
          
          <div className="relative z-10 grid grid-cols-2 sm:grid-cols-4 gap-8 text-center">
            {[
              { value: '19+', label: 'Sacred Spreads', symbol: '𒊹' },
              { value: '3', label: 'Deck Traditions', symbol: '𒄑' },
              { value: '12', label: 'Zodiac Houses', symbol: '☉' },
              { value: '7', label: 'Chakra Gates', symbol: '𒀭' }
            ].map((stat, i) => (
              <motion.div 
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="group"
              >
                <div className="text-babylon-gold/40 text-2xl mb-2 group-hover:text-babylon-gold/60 transition-colors">
                  {stat.symbol}
                </div>
                <p className="font-heading text-3xl sm:text-4xl text-babylon-gold mb-1">{stat.value}</p>
                <p className="text-babylon-text-dim text-xs">{stat.label}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* Bug Tracker Section */}
      <section className="relative z-10 px-4 sm:px-6 py-12 max-w-3xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <BugTrackerWidget />
        </motion.div>
      </section>

      {/* Footer */}
      <footer className="relative z-10 px-4 sm:px-6 py-10 border-t border-babylon-gold/10">
        <div className="max-w-5xl mx-auto">
          <div className="mb-8"><TipJar compact /></div>
          
          {/* Cuneiform border */}
          <div className="flex items-center justify-center gap-2 mb-6 text-babylon-gold/30">
            <span>𒀭</span>
            <div className="flex-1 h-px bg-gradient-to-r from-transparent via-babylon-gold/20 to-transparent" />
            <span>𒊹</span>
            <div className="flex-1 h-px bg-gradient-to-r from-transparent via-babylon-gold/20 to-transparent" />
            <span>𒀭</span>
          </div>
          
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
            <div className="flex items-center gap-3">
              <Sparkles className="w-5 h-5 text-babylon-gold" />
              <span className="font-heading text-sm text-babylon-text">CosmiCards</span>
              <span className="text-babylon-gold/50 text-[10px] font-mono ml-2">by T3B3TU</span>
            </div>
            <div className="flex items-center gap-6 text-xs">
              <Link to="/support" className="text-babylon-text-dim hover:text-babylon-gold transition-colors">Support</Link>
              <span className="text-babylon-text-dim/50">© {new Date().getFullYear()}</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};
